/** @jsxImportSource @emotion/react */

import { CalciteLoader } from "@esri/calcite-components-react";
import { Button } from "components/Button";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { ReactTabulator } from "react-tabulator";
import { getAliColumns, getAliExportColumns } from "services/ALIRecord.d";
import tw from "twin.macro";

import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css";
import "react-tabulator/lib/styles.css";

import FiltersDisplay from "components/FiltersDisplay";
import React, { useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Criteria } from "services/APIBase.d";
import { Msag } from "services/MSAGRecord.d";
import RecordService from "services/RecordService";
import { updateFooterText } from "utils/helpers";
import ExportButton from "../../components/ExportButton";

const client = new RecordService("ali");
const msagClient = new RecordService("msag");
const ALIRecords: React.FC = () => {
  const [msagRecord, setMsagRecord] = useState<Msag | null>(null);
  const { user } = useContext(UserContext);
  const [pageSize, setPageSize] = useState<number>(25);
  const [criterias, setCriterias] = useState<Criteria[]>([]);
  const [isTableReady, setIsTableReady] = useState(false);

  const [filters, setFilters] = useState<
    Array<{ field: string; value: string }>
  >([]);
  const hasWritePermission = user?.functions
    ? user.functions.includes("foresite:ali:write")
    : false;

  const hasChangeRequestPermission = user?.functions
    ? user.functions.includes("foresite:ali:requestChange")
    : false;

  const [aliCols] = useState(getAliColumns(user?.functions || []));

  const fetchMsagRecord = async (msagId: string) => {
    try {
      const res = await msagClient.getRecords(
        {
          criterias: [{ field: "id", type: "=", value: msagId }],
        },
        "search",
      );
      if (res.data.message.List && res.data.message.List.length > 0) {
        setMsagRecord(res.data.message.List[0]);
      }
    } catch (error) {
      console.error("Error fetching MSAG record:", error);
    }
  };

  useEffect(() => {
    // loading filters and criterias
    // CRITERIAS: all parameters passed to the API (e.g. header filters, CPN Range from Dashboard)
    // FILTERS: text input in column headers only
    // Current additional criteria is only BETWEEN type for CPN Range from Quick Start

    // Try to restore filters from localStorage
    const savedFilters = localStorage.getItem("aliRecords-criteria");
    if (savedFilters) {
      console.log("Restoring filters from localStorage", savedFilters);
      try {
        const filters = JSON.parse(savedFilters);
        if (Array.isArray(filters) && filters.length > 0) {
          // Separate BETWEEN filters into criteria
          const betweenFilters = filters.filter((f) => f.type === "BETWEEN");
          if (betweenFilters.length > 0) {
            setCriterias(betweenFilters);
          }
          // Separate msag_id filters into criteria
          const msagIdFilter = filters.filter((f) => f.field === "msag_id");
          if (msagIdFilter.length > 0) {
            setCriterias(msagIdFilter);
            // Fetch MSAG record details
            fetchMsagRecord(msagIdFilter[0].value);
          }
          // Set remaining filters as initial filters
          const headerFilters = filters.filter(
            (f) => f.type !== "BETWEEN" && f.field !== "msag_id",
          );
          if (headerFilters.length > 0) {
            setFilters(headerFilters);
          }
        }
      } catch (e) {
        console.error("Error restoring filters:", e);
      }
    }
    setIsTableReady(true);
  }, []);

  const navigate = useNavigate();
  const clickRow = (e, row) => {
    const data = row.getData();
    const cpn = data.CPN;

    if (cpn) {
      navigate(`${cpn}`);
    }
  };

  const tableRef = useRef<any>(null);
  // set tabulator options
  const options = {
    //renderVertical: "basic",
    layout: "fitColumns",
    movableColumns: true,
    placeholder: "No ALI Records Found",
    // persist table state
    persistenceID: "aliRecords1", // update ID param whenever table columns are updated
    persistenceMode: true, // store table state in local storage if possible, if not store in cookie
    persistence: {
      columns: ["width"], // persist column width; also persists column order
      page: true, // persist page number
    },
    // pagination
    pagination: true, // enable pagination
    paginationMode: "remote", // enable remote pagination
    paginationSize: pageSize, // default number of rows per page
    paginationSizeSelector: [10, 25, 50, 100], // page size options
    footerElement: "<span id='record-count'></span>",
    // filter
    filterMode: "remote", // send filter data to the server instead of processing locally
    initialHeaderFilter: filters.filter((f) => f.field !== "msag_id"),
    // sorting
    sortMode: "remote",
    initialSort: [{ column: "CPN", dir: "asc" }],
    // data request
    ajaxRequestFunc: function (url, config, params) {
      // convert default table params to API params
      let sortField = params.sort.length > 0 ? params.sort[0].field : "CPN";
      let sortDir = params.sort.length > 0 ? params.sort[0].dir : "asc";
      let firstRow = (params.page - 1) * params.size;
      let rangeStr = firstRow + "," + params.size;

      let savedCriterias = localStorage.getItem("aliRecords-criteria");
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) {
        crit = JSON.parse(savedCriterias).filter(
          (c) => c.type === "BETWEEN" || c.field === "msag_id",
        );
      }

      //replace any privateCit filter values from True/False to 1/0 for weirdness with tabulator
      params = {
        ...params,
        filter: params.filter.map((f) => {
          if (f.field === "privateCit") {
            return {
              ...f,
              value:
                f.value === "True" ? "1" : f.value === "False" ? "0" : f.value,
            };
          }
          return f;
        }),
      };

      let reqBody = {
        range: rangeStr,
        sortBy: sortField,
        sortDirection: sortDir,
        criterias: [
          ...crit,
          ...params.filter.map((f) => ({
            field: f.field,
            type: f.type,
            value: f.value,
          })),
        ],
      };

      // log request body
      console.log("ALI table request body", reqBody);
      return new Promise(function (resolve, reject) {
        // fetch records
        client
          .getRecords(reqBody, url, undefined, false)
          .then((res) => {
            // success -> return data
            resolve(res.data);
          })
          .catch((err) => {
            // error -> return error message
            console.error("ALI table error response", err);
            reject(err);
          });
      });
    },
    ajaxURL: "searchByCriteria", // API path
    ajaxResponse: (url, params, response) => {
      // log response
      console.log("ALI table response", response);
      // pagination footer text
      let currPageSize = tableRef.current.getPageSize();
      let currPage = tableRef.current.getPage();
      updateFooterText(
        currPage,
        currPageSize,
        response.message.size,
        response.message.totalRecordCount,
      );
      let savedCriterias = localStorage.getItem("aliRecords-criteria");
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) {
        crit = JSON.parse(savedCriterias).filter(
          (c) => c.type === "BETWEEN" || c.field === "msag_id",
        );
      }

      // Store current filters if they exist
      let crits = [
        ...crit,
        ...params.filter.map((f) => ({
          field: f.field,
          type: f.type,
          value: f.value,
        })),
      ];

      if (crits && crits.length > 0) {
        console.log("Storing current criteria", crits);
        localStorage.setItem("aliRecords-criteria", JSON.stringify(crits));
        setCriterias(crits);
      } else {
        console.log("Removing criteria");
        localStorage.removeItem("aliRecords-criteria");
        setCriterias([]);
      }

      // return data to table
      let d = response.message.size > 0 ? response.message.list : [];
      let last =
        response.message.size > 0
          ? Math.ceil(response.message.totalRecordCount / currPageSize)
          : 0;
      return {
        data: d,
        last_page: last,
      };
    },
  };

  const handleClearFilters = () => {
    localStorage.removeItem("aliRecords-criteria");
    setCriterias([]);
    setFilters([]);
    setMsagRecord(null);
    tableRef.current?.clearHeaderFilter();
    console.log("Clear filters");
  };

  return (
    <>
      <div css={[tw`flex md:flex-nowrap flex-wrap justify-between `]}>
        <div css={[tw`flex flex-col space-y-4 w-full`]}>
          {criterias.some(
            (c) => c.type === "BETWEEN" && typeof c.value === "string",
          ) && (
            <div css={[tw`text-sm text-gray-600 font-medium`]}>
              {criterias
                .filter(
                  (c) => c.type === "BETWEEN" && typeof c.value === "string",
                )
                .map((c, index) => {
                  const column = aliCols.find((col) => col.field === c.field);
                  const [start, end] = (c.value as string).split(",");
                  // Format CPN values as telephone numbers
                  const formatValue = (value: string) => {
                    if (c.field === "CPN") {
                      // Remove any non-digit characters and format as (XXX) XXX-XXXX
                      const digits = value.replace(/\D/g, "");
                      if (digits.length === 10) {
                        return `(${digits.slice(0, 3)}) ${digits.slice(
                          3,
                          6,
                        )}-${digits.slice(6)}`;
                      }
                    }
                    return value;
                  };
                  return (
                    <span key={`${c.field}-${index}`}>
                      <span css={[tw`font-bold`]}>
                        {column?.title || c.field === "CPN"
                          ? "Telephone Number Range"
                          : c.field}
                        :
                      </span>{" "}
                      {`${formatValue(start)} - ${formatValue(end)}`}
                      {index <
                      criterias.filter(
                        (c) =>
                          c.type === "BETWEEN" && typeof c.value === "string",
                      ).length -
                        1
                        ? " | "
                        : ""}
                    </span>
                  );
                })}
            </div>
          )}
          {msagRecord && (
            <div css={[tw`text-sm text-gray-600 font-medium`]}>
              <span>
                <span css={[tw`font-bold`]}>MSAG:</span>{" "}
                {`${msagRecord.LowRange || ""} - ${msagRecord.HiRange || ""} ${
                  msagRecord.PrefixDir || ""
                } ${msagRecord.StreetName || ""} ${
                  msagRecord.StreetSuffix || ""
                } ${msagRecord.PostDir || ""} ${msagRecord.MCN || ""} ${
                  msagRecord.State || ""
                }`}
              </span>
            </div>
          )}
          <div css={[tw`flex justify-between`]}>
            <div css={[tw`space-x-4 flex items-center`]}>
              <Button
                iconStart="search"
                onClick={() => tableRef.current?.setData()}
                variant="outline"
                kind="brand"
              >
                Filter
              </Button>
              <Button
                iconStart="x"
                id="clear-button"
                onClick={handleClearFilters}
                variant="outline"
                kind="brand"
                disabled={criterias.length === 0}
              >
                Clear
              </Button>
              <FiltersDisplay criterias={criterias} cols={aliCols} />
            </div>
            <div css={[tw`space-x-4 flex items-center`]}>
              {hasWritePermission && (
                <Button
                  iconStart="add-in-new"
                  onClick={() => navigate("new")}
                  variant="solid"
                  kind="brand"
                >
                  New
                </Button>
              )}
              {hasWritePermission && (
                <Button
                  iconStart="phone"
                  onClick={() => navigate("migrate")}
                  variant="solid"
                  kind="brand"
                >
                  Migrate
                </Button>
              )}
              {hasChangeRequestPermission && (
                <Button
                  iconStart="configure"
                  onClick={() =>
                    navigate("bulk-change-request", { state: { criterias } })
                  }
                  variant="solid"
                  kind="brand"
                >
                  Bulk Change Request
                </Button>
              )}
              <ExportButton
                exportColumns={getAliExportColumns()}
                criterias={criterias}
                type="ali-record"
              />
              {/* <SOIReportButton criterias={criterias} /> */}
            </div>
          </div>
        </div>
      </div>

      <div css={[tw` min-h-full border border-gray-300 rounded-md`]}>
        {isTableReady ? (
          <ReactTabulator
            id="aliLoader"
            onRef={(r) => {
              tableRef.current = r.current;
            }}
            events={{
              pageSizeChanged: function (pagesize) {
                setPageSize(pagesize);
              },
            }}
            options={options}
            columns={aliCols}
          />
        ) : (
          <div css={[tw`flex justify-center items-center h-32`]}>
            <CalciteLoader scale="s" label="Loading records..." />
          </div>
        )}
      </div>
      <Outlet />
    </>
  );
};

export default ALIRecords;
